import React, { useEffect, useRef, useState } from 'react';
import { FiChevronDown, FiChevronUp, FiColumns, FiRotateCcw } from 'react-icons/fi';
import { toggleableColumns } from '../config/listViewColumns';
import IconButton from './IconButton';
import './ListColumnPicker.css';

export default function ListColumnPicker({
  allColumns,
  visibleColumnIds,
  onToggle,
  onMove,
  onReset,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const options = toggleableColumns(allColumns);
  const unlockedVisible = visibleColumnIds.filter((id) =>
    options.some((col) => col.id === id)
  );

  const orderedOptions = [
    ...unlockedVisible
      .map((id) => options.find((col) => col.id === id))
      .filter(Boolean),
    ...options.filter((col) => !visibleColumnIds.includes(col.id)),
  ];

  return (
    <div className="list-column-picker" ref={wrapRef}>
      <IconButton
        icon={FiColumns}
        label="Choose columns"
        active={open}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
      />
      {open && (
        <div className="list-column-picker-panel" role="dialog" aria-label="Choose visible columns">
          <div className="list-column-picker-head">
            <span>Columns</span>
            <button type="button" className="list-column-picker-reset" onClick={onReset}>
              <FiRotateCcw aria-hidden /> Reset
            </button>
          </div>
          <p className="list-column-picker-hint">Show, hide, or reorder columns</p>
          <ul className="list-column-picker-list">
            {orderedOptions.map((col) => {
              const visible = visibleColumnIds.includes(col.id);
              const unlockIdx = unlockedVisible.indexOf(col.id);
              const canMoveUp = visible && unlockIdx > 0;
              const canMoveDown = visible && unlockIdx >= 0 && unlockIdx < unlockedVisible.length - 1;
              return (
                <li key={col.id}>
                  <div className="list-column-picker-item">
                    <label className="list-column-picker-check">
                      <input
                        type="checkbox"
                        checked={visible}
                        onChange={() => onToggle(col.id)}
                      />
                      <span>{col.label}</span>
                    </label>
                    {typeof onMove === 'function' && (
                      <div className="list-column-picker-move">
                        <button
                          type="button"
                          className="list-column-picker-move-btn"
                          aria-label={`Move ${col.label} earlier`}
                          disabled={!canMoveUp}
                          onClick={() => onMove(col.id, 'up')}
                        >
                          <FiChevronUp aria-hidden />
                        </button>
                        <button
                          type="button"
                          className="list-column-picker-move-btn"
                          aria-label={`Move ${col.label} later`}
                          disabled={!canMoveDown}
                          onClick={() => onMove(col.id, 'down')}
                        >
                          <FiChevronDown aria-hidden />
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
